import Vue from 'vue'
import Router from 'vue-router'

Vue.use(Router)

function isJustTogglingModal(to, from) {
  return (
    from.name === 'catalog' && to.name === 'product'
        || to.name === 'catalog' && from.name === 'product'
        || ['orders', 'addresses'].some(nameStart => from.name?.startsWith(nameStart) && to.name.startsWith(nameStart))
  );
}

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/pages/HomePage.vue'),
    },
    {
      path: '/catalog/:categoryId?/:categorySlug?',
      name: 'catalog',
      component: () => import('@/pages/CatalogPage.vue'),
      props: true,
    },
    {
      path: '/categories/:categoryId?',
      name: 'categories',
      component: () => import('@/pages/Categories.vue'),
      props: true,
    },
    {
      path: '/promotions/:promotionId/rewards/:rewardId',
      name: 'promotions.rewards',
      component: () => import('@/pages/Promotions/PromotionRewardsPage.vue'),
      props: true,
    },
    {
      path: '/more',
      name: 'more',
      component: () => import('@/pages/More.vue'),
    },
    {
      path: '/',
      name: 'profile',
      component: () => import('@/pages/profile/AuthRequiringPage'),
      children: [
        {
          path: '/orders/:id?',
          name: 'orders',
          props: true,
          component: () => import('@/pages/profile/Orders'),
        },

        {
          path: '/addresses/',
          name: 'addresses.list',
          props: r => ({ action: 'list' }),
          component: () => import('@/pages/profile/Addresses'),
        },
        {
          path: '/addresses/new',
          name: 'addresses.create',
          props: r => ({ action: 'create' }),
          component: () => import('@/pages/profile/Addresses'),
        },
        {
          path: '/addresses/:id',
          name: 'addresses.edit',
          props: r => ({ action: 'edit', id: r.params.id }),
          component: () => import('@/pages/profile/Addresses'),
        },
        {
          path: '/dashboard',
          name: 'dashboard',
          component: () => import('@/pages/profile/Dashboard'),
        },
      ],
    },
    {
      path: '/legal/privacy-policy',
      name: 'legal-privacy-policy',
      component: () => import('@/pages/Legal/PrivacyPolicyPage'),
    },
    {
      path: '/legal/personal-data-policy',
      name: 'legal-personal-data-policy',
      component: () => import('@/pages/Legal/PersonalDataPolicy'),
    },
    {
      path: '/legal/personal-data-agreement',
      name: 'legal-personal-data-agreement',
      component: () => import('@/pages/Legal/PersonalDataAgreement'),
    },
    {
      path: '/legal/eula',
      name: 'legal-eula',
      component: () => import('@/pages/Legal/Eula.vue'),
    },
    {
      path: '/legal/delete-account',
      name: 'legal-delete-account',
      component: () => import('@/pages/Legal/DeleteAccountPage.vue'),
    },
    {
      path: '/product/:productId/:productSlug?',
      redirect: '/products/:productId',
    },
    {
      path: '/recaptcha/:action',
      name: 'recaptcha',
      component: () => import('@/pages/Recaptcha'),
      meta: { layout: 'empty' },
    },
    {
      path: '/payment/cloudpayments/:order/:token',
      name: 'payment.cloudpayments',
      component: () => import('@/pages/Orders/Payments/Cloudpayments/CloudpaymentsWidgetPage'),
      meta: { layout: 'empty' },
    },
    {
      path: '/payment/cloudpayments/:order/:token/:status',
      name: 'payment.cloudpayments.status',
      component: () => import('@/pages/Orders/Payments/Cloudpayments/CloudpaymentsStatusPage'),
      meta: { layout: 'empty' },
    }
  ],

  scrollBehavior(to, from, savedPosition) {
    // If we've only opened the product sidebar, without changing anything else.
    if (isJustTogglingModal(to, from)) {
      // Otherwise if we close the details view on addresses or orders page on a wide screen, it would scroll back
      // to the position at which it was opened.
      // Not sure, but https://github.com/vuejs/vue-router/issues/2095 may be the problem.
      const scrollRestorationBefore = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      const unhook = this.afterEach(() => {
        history.scrollRestoration = scrollRestorationBefore;
        unhook();
      });

      return false;
    }

    return (async () => {
      // Yeah, a bit hacky and WET.
      function isRootRouterViewTransitionTriggered(to, from) {
        if (from.matched[0] === undefined) {
          return false
        }

        return to.matched[0].instances !== from.matched[0].instances;
      }

      if (isRootRouterViewTransitionTriggered(to, from)) {
        await new Promise(resolve => router.app.$root.$once('router-view-transition-leave', resolve));
      }

      if (savedPosition) {
        return savedPosition
      }

      return { x: 0, y: 0 }
    })();
  },
})

// Adding imperatively because we need to have access to the instance withing this route's functions.
router.addRoutes([
  {
    path: '/products/:productId/:productSlug?',
    name: 'product',
    component: () => import('@/pages/CatalogPage'),
    props(route) {
      let categoryId;
      // Even though the `categoryId` param is not present in the URL, it still must be provided
      // programatically. Can be `null` / `undefined`, for us to show the full products list.
      if ('categoryId' in route.params) {
        categoryId = route.params.categoryId;
      } else {
        console.warn('Exprected `categoryId` property to be present. Defaulting to `null`');
        categoryId = null;
      }
      return {
        sidebarProductId: route.params.productId,
        categoryId,
      };
    },
    beforeEnter(to, from, next) {
      // E.g. if we're coming from a different page, or directly opening a link.
      if (!isJustTogglingModal(to, from)) {
        router.replace({ name: 'catalog' }, () => {
          router.push({ ...to });
          next(false);
        });
        return;
      }

      if ('categoryId' in to.params) {
        return next();
      } else {
        // This is correct, but there is a bug https://github.com/vuejs/vue-router/issues/2814
        // const newTo = { ...to };
        // newTo.params.categoryId = from.params.categoryId ? from.params.categoryId : null;
        // return next(newTo);

        to.params.categoryId = from.params.categoryId ? from.params.categoryId : null;
        return next();
      }
    },
  },
])

export default router;
